/*
 * Print templates are ordinary React components with print CSS (PLAN.md §7).
 * Everything here is black on white: thermal printers have no colour, laser printers
 * in a clinic are usually mono, and a fax of a receipt still has to be readable.
 *
 * Sheet sizes are in millimetres so what is on screen is the size of the paper.
 * The matching @page size lives in Print.tsx (PAPER); change both together.
 */
.sheet {
  box-sizing: border-box;
  background: var(--paper);
  color: var(--ink);
  font-family: var(--font-sans);
  font-weight: 400;
  /* On screen the sheet is a preview; the rule shows where the paper ends. */
  outline: 1px solid var(--rule);
}

.sheet[data-paper='receipt-80mm'] {
  width: 80mm;
  padding: 4mm;
  font-size: 9pt;
  line-height: 1.35;
}

.sheet[data-paper='a5'] {
  width: 148mm;
  min-height: 210mm;
  padding: 10mm;
  font-size: 10pt;
  line-height: 1.4;
}

.sheet[data-paper='a4'] {
  width: 210mm;
  min-height: 297mm;
  padding: 12mm;
  font-size: 10pt;
  line-height: 1.45;
}

.sheet[data-paper='label-50x25'] {
  width: 50mm;
  height: 25mm;
  padding: 1.5mm 2mm;
  font-size: 7pt;
  line-height: 1.2;
  overflow: hidden;
}

.sheet[data-paper='wristband'] {
  display: flex;
  align-items: center;
  gap: 10mm;
  width: 279mm;
  height: 25mm;
  padding: 2mm 6mm;
  font-size: 9pt;
  line-height: 1.25;
  overflow: hidden;
}

@media print {
  .sheet {
    outline: 0;
  }
}

/* Header: who printed it, then what it is. */
.header {
  margin-bottom: 3mm;
  padding-bottom: 2mm;
  border-bottom: 1px solid var(--ink);
}

.facility {
  font-weight: 700;
  font-size: 1.15em;
}

.facilityLine {
  color: var(--ink);
  font-size: 0.85em;
}

.title {
  margin-top: 1.5mm;
  font-weight: 600;
}

.sheet[data-paper='receipt-80mm'] .header,
.sheet[data-paper='label-50x25'] .header {
  text-align: center;
}

/* Label and value pairs. */
.lines {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.8mm 4mm;
  margin: 0 0 3mm;
}

/* Each pair sits in the grid the list defines, not in a box of its own. */
.linePair {
  display: contents;
}

.lineLabel {
  margin: 0;
  color: var(--ink);
  font-size: 0.85em;
}

.lineValue {
  margin: 0;
  font-weight: 600;
}

/* Identifiers are read character by character, so they use the mono face. */
.mono {
  font-family: var(--font-id);
}

.table {
  width: 100%;
  margin-bottom: 3mm;
  border-collapse: collapse;
}

.table th {
  padding: 1mm 0;
  border-bottom: 1px solid var(--ink);
  font-weight: 600;
  font-size: 0.85em;
  text-align: left;
}

.table td {
  padding: 1mm 0;
  border-bottom: 1px solid var(--rule);
  vertical-align: top;
}

.table th + th,
.table td + td {
  padding-left: 3mm;
}

.table th[data-numeric],
.table td[data-numeric] {
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.table tfoot td {
  border-top: 1px solid var(--ink);
  border-bottom: 0;
  font-weight: 700;
}

/* The space a barcode is printed into. Kept empty and clear of ink so any scanner
   can read the symbol, with the value underneath for a person to read or type. */
.barcode {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 10mm;
  margin-bottom: 1mm;
  border: 1px dashed var(--rule);
  background: var(--paper);
}

.barcode[data-tall] {
  height: 15mm;
}

.barcodeHint {
  color: var(--pencil);
  font-size: 0.75em;
}

@media print {
  /* On paper the area is blank: a dashed box would be read as part of the symbol. */
  .barcode {
    border: 0;
  }

  .barcodeHint {
    display: none;
  }
}

.barcodeValue {
  display: block;
  font-family: var(--font-id);
  font-size: 0.95em;
  letter-spacing: 0.06em;
  text-align: center;
}

.footer {
  margin-top: 3mm;
  padding-top: 2mm;
  border-top: 1px solid var(--rule);
  font-size: 0.85em;
}

.sheet[data-paper='receipt-80mm'] .footer {
  text-align: center;
}
